import React, { RefObject } from 'react'
import { Box, Typography } from '@mui/material'
import { dateUtils } from 'src/utils/date.util'
import { useRatesDataGridContext } from '../contexts/ratesDataGrid/RatesDataGridContext'

// Icons
import GridArrowStepperBtn from 'src/applications/rates/components/GridArrowStepperBtn'
import DateDateRangePicker from 'src/components/dateDateRangePicker/DateDateRangePicker'
import { useDesktopAlertContext } from 'src/contexts/DesktopAlert.context'
import { useTranslation } from 'react-i18next'

const DayListBar = ({
  rightColumnEl,
  scrollableEl,
  leftColumnEl,
  rightColumnWidth,
  onScroll
}: {
  rightColumnEl: React.MutableRefObject<any>
  scrollableEl: RefObject<HTMLDivElement>
  leftColumnEl: React.MutableRefObject<any>
  rightColumnWidth: number | undefined
  onScroll?: (value: number) => void
}) => {
  const { daysList, dateRange, changeDateRange } = useRatesDataGridContext()
  const { alert } = useDesktopAlertContext()
  const { t } = useTranslation()

  const cellWidth = (rightColumnWidth && rightColumnWidth / 14) ?? 0

  const handleCalendarScroll = (value?: number) => {
    if (!value) return
    if (!scrollableEl.current) return
    const newValue = scrollableEl.current.scrollLeft + value
    scrollableEl.current.scrollLeft = newValue
    onScroll?.(value)
  }

  const handleDateRangeChange = ([iniDate, endDate]: [
    Date | null,
    Date | null
  ]) => {
    const newIniDate = iniDate
    let newEndDate = endDate
    if (!newIniDate || !newEndDate) return
    const monthsDiff = dateUtils.getMonthsDiff({
      date1: newIniDate,
      date2: newEndDate,
      getDecimals: true
    })
    if (monthsDiff > 2) {
      newEndDate = dateUtils.addToDate(new Date(newIniDate), {
        value: 2,
        type: 'months'
      })
      alert({
        message: t('alerts.rates_grid_max_months', {
          ns: 'tarifas'
        }),
        type: 'warning'
      })
    }
    changeDateRange([newIniDate, newEndDate])
  }

  return (
    <>
      <Box
        sx={{
          marginTop: '10px',
          zIndex: '2',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          boxShadow: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: '36%',
            display: 'flex',
            alignItems: 'center'
          }}
          ref={leftColumnEl}
        >
          <Box
            sx={{
              width: '280px',
              marginLeft: '1rem'
            }}
          >
            <DateDateRangePicker
              onChange={(newValue) => {
                handleDateRangeChange(newValue)
              }}
              value={dateRange}
              minDate={new Date()}
              localeText={{
                start: '',
                end: ''
              }}
              slotProps={{
                fieldSeparator: {
                  margin: '.5rem !important'
                },
                textField: {
                  sx: {
                    marginLeft: '0 !important'
                  }
                }
              }}
              autoFocus
            />
          </Box>
          <Box
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              marginRight: '1rem'
            }}
          >
            <Box>
              <GridArrowStepperBtn
                isDouble
                onClick={() => handleCalendarScroll(-(cellWidth * 14))}
              />
              <GridArrowStepperBtn
                onClick={() => handleCalendarScroll(-(cellWidth * 7 ?? 0))}
              />
            </Box>
            <Box
              sx={{
                display: {
                  xs: 'block',
                  xl: 'none'
                }
              }}
            >
              <GridArrowStepperBtn
                onClick={() => handleCalendarScroll(cellWidth * 7)}
                direction='forward'
              />
              <GridArrowStepperBtn
                onClick={() => handleCalendarScroll(cellWidth * 14)}
                direction='forward'
                isDouble
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: {
              xs: '64%',
              xl: '60%'
            }
          }}
          ref={rightColumnEl}
        >
          <Box
            ref={scrollableEl}
            sx={{
              width: '100%',
              alignItems: 'center',
              overflowX: 'auto',
              overflowY: 'hidden',
              display: 'flex',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              '& > *:last-of-type': {
                borderRight: '1px solid gray'
              }
            }}
          >
            {daysList.map((date) => {
              const dayOfWeek = dateUtils.dayOfWeek(date)
              const isWeekend = dayOfWeek === 4 || dayOfWeek === 5
              return (
                <Box
                  key={date.toDateString()}
                  sx={{
                    width: cellWidth,
                    minWidth: cellWidth,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    userSelect: 'none',
                    borderLeft: '1px solid gray',
                    ...(isWeekend && {
                      bgcolor: 'primary.100'
                    })
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '9px'
                    }}
                  >
                    {dateUtils.getDayOfWeekText(date)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '1em',
                      fontWeight: '500'
                    }}
                  >
                    {dateUtils.toDayString(date)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '8px',
                      textTransform: 'uppercase'
                    }}
                  >
                    {dateUtils.toMonthStr(date)}
                  </Typography>
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box
          sx={{
            display: {
              xs: 'none',
              xl: 'flex'
            }
          }}
        >
          <GridArrowStepperBtn
            onClick={() => handleCalendarScroll(cellWidth * 7)}
            direction='forward'
          />
          <GridArrowStepperBtn
            onClick={() => handleCalendarScroll(cellWidth * 14)}
            direction='forward'
            isDouble
          />
        </Box>
      </Box>
    </>
  )
}

export default DayListBar
